import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@/types';
import { signInHref } from '@/lib/login-next';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      if (typeof window !== 'undefined') {
        // Impersonation escape hatch: a 401 while impersonating means the
        // impersonation token died (expired, revoked, or the target account was
        // deactivated). Don't bounce the admin to /login — restore their own
        // token and drop them back on the dashboard. The persisted zustand pair
        // is dropped too, so the stale impersonation token can't be rehydrated
        // over the restored one.
        const originalToken = localStorage.getItem('original_token');
        if (originalToken) {
          localStorage.setItem('token', originalToken);
          localStorage.removeItem('original_token');
          localStorage.removeItem('auth-storage');
          window.location.href = '/';
          return Promise.reject(error);
        }

        // Clear token and redirect to login
        localStorage.removeItem('token');
        // Only redirect if not already on login page. Public patient-facing
        // pages (/quote/<token>) must never bounce to the staff login — a
        // stray 401 there (e.g. a protected fetch from a global provider)
        // just fails silently instead of hijacking the page.
        if (
          !window.location.pathname.includes('/login') &&
          !window.location.pathname.startsWith('/quote/')
        ) {
          // A push tap on a phone whose session was replaced lands here
          // first: keep the page so signing back in opens the record. A
          // session that ends mid-use still goes to a plain /login.
          window.location.href = signInHref(window.location.pathname + window.location.search);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
